import React, { createContext, useState, useCallback, useEffect, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import { get } from '../api';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const initialToken = localStorage.getItem('token');
  const [token, setToken] = useState(initialToken);
  const [user, setUser] = useState(() => {
    if (initialToken && typeof initialToken === 'string' && initialToken.trim() !== '') {
      try {
        const decoded = jwtDecode(initialToken);
        // Check if token is expired
        if (decoded.exp && decoded.exp * 1000 < Date.now()) {
          console.log('Token expired, clearing...');
          localStorage.removeItem('token');
          return null;
        }
        return decoded;
      } catch (error) {
        console.error('Error decoding initial token:', error);
        localStorage.removeItem('token');
        return null;
      }
    }
    return null;
  });
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
    setIsImpersonating(false);
    // State will be cleared, component will re-render and show login
  }, []);

  const forceLogout = useCallback(() => {
    // Clear all possible storage
    localStorage.clear();
    sessionStorage.clear();
    // Clear cookies
    document.cookie.split(";").forEach(function(c) { 
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
    });
    // Reset all state
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
    setIsImpersonating(false);
    // State will be cleared, component will re-render and show login
  }, []);

  const login = useCallback((newToken, forceChange = false) => {
    if (!newToken || typeof newToken !== 'string' || newToken.trim() === '') {
      console.error('Invalid token provided to login function');
      return;
    }
    
    try {
      const decodedUser = jwtDecode(newToken);
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(decodedUser);
      setMustChangePassword(forceChange);
    } catch (error) {
      console.error('Error decoding token during login:', error);
      // Don't set invalid tokens
      return;
    }
  }, []);

  const impersonate = useCallback((newToken) => {
    if (!newToken || typeof newToken !== 'string' || newToken.trim() === '') {
      console.error('Invalid token provided to impersonate function');
      return;
    }
    
    try {
      const decodedUser = jwtDecode(newToken);
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(decodedUser);
      setMustChangePassword(false);
      setIsImpersonating(true);
      console.log('✅ Impersonation successful:', decodedUser.email);
    } catch (error) {
      console.error('Error decoding impersonation token:', error);
      return;
    }
  }, []);

  const safeDecodeToken = useCallback((jwt) => {
    if (!jwt || typeof jwt !== 'string' || jwt.trim() === '') {
      return null;
    }
    try {
      return jwtDecode(jwt);
    } catch (decodeError) {
      console.warn('Failed to decode token during fallback refresh:', decodeError);
      return null;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const response = await get('/auth/me');
      setUser(response.data);
    } catch (err) {
      const status = err?.response?.status;
      const isAuthFailure = status === 401 || status === 403;
      const isNetworkError = err?.code === 'ECONNABORTED' || err?.message === 'Network Error' || !err?.response;

      console.error('User refresh failed:', {
        message: err?.message,
        code: err?.code,
        status,
        isAuthFailure,
        isNetworkError
      });

      if (isAuthFailure) {
        // Only force logout if the token is truly invalid/unauthorized
        forceLogout();
        return;
      }

      // For transient/network issues, keep the existing decoded token data
      const fallbackUser = safeDecodeToken(token);
      if (fallbackUser) {
        setUser((current) => current ?? fallbackUser);
      }
    }
  }, [token, forceLogout, safeDecodeToken]);

  useEffect(() => {
    if (token && !isImpersonating) {
      refreshUser();
    }
  }, [token, refreshUser, isImpersonating]);

  // Check token validity on app startup
  useEffect(() => {
    if (initialToken) {
      try {
        const decoded = jwtDecode(initialToken);
        // If token is expired, clear it immediately
        if (decoded.exp && decoded.exp * 1000 < Date.now()) {
          console.log('Initial token expired, clearing...');
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      } catch (error) {
        console.error('Invalid initial token, clearing...');
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
    }
  }, [initialToken]);

  useEffect(() => {
    if (!token) return;

    let timeoutId;

    // Logout after 30 minutes (1800000 ms) of inactivity
    const handleActivity = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        logout();
        alert('You have been logged out due to inactivity.');
      }, 30 * 60 * 1000); // 30 minutes
    };

    // List of events that indicate user activity
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart'];

    // Attach event listeners
    events.forEach(event =>
      window.addEventListener(event, handleActivity)
    );

    // Start the timer initially
    handleActivity();

    // Cleanup on unmount or logout
    return () => {
      clearTimeout(timeoutId);
      events.forEach(event =>
        window.removeEventListener(event, handleActivity)
      );
    };
  }, [token, logout]);

  const value = useMemo(() => ({
    token,
    user,
    mustChangePassword,
    isImpersonating,
    login,
    logout,
    forceLogout,
    refreshUser,
    impersonate
  }), [token, user, mustChangePassword, isImpersonating, login, logout, forceLogout, refreshUser, impersonate]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}